import type { Theme } from '@mui/material/styles';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TEMPERATURE_THRESHOLDS } from '../types';

dayjs.extend(utc);

export type TemperatureStatus = 'Critical' | 'Warning' | 'Incident' | 'Normal';

export interface TemperatureInfo {
  status: TemperatureStatus;
  color: string;
  severity: number;
}

export interface ChartStatistics {
  min: number;
  max: number;
  avg: number;
  median: number;
  stdDev: number;
  count: number;
  latest: number;
  totalPoints: number;
  percentile95: number;
  percentile99: number;
}

export interface TimeInterval {
  value: number;
  unit: 'second' | 'minute' | 'hour' | 'day';
}

const DEFAULT_TARGET_POINTS = 1000;

const TEMPERATURE_SEVERITY: Record<TemperatureStatus, number> = {
  Critical: 3,
  Warning: 2,
  Incident: 1,
  Normal: 0,
} as const;

const TIME_UNIT_MS = {
  second: 1000,
  minute: 60 * 1000,
  hour: 60 * 60 * 1000,
  day: 24 * 60 * 60 * 1000,
} as const;

export function getTemperatureStatus(temperature: number): TemperatureStatus {
  if (temperature >= TEMPERATURE_THRESHOLDS.CRITICAL) {
    return 'Critical';
  }
  if (temperature >= TEMPERATURE_THRESHOLDS.WARNING) {
    return 'Warning';
  }
  if (temperature >= TEMPERATURE_THRESHOLDS.INCIDENT) {
    return 'Incident';
  }
  return 'Normal';
}

export function getTemperatureInfo(
  temperature: number | null | undefined,
  theme: Theme,
): TemperatureInfo | null {
  if (temperature == null || !Number.isFinite(temperature)) {
    return null;
  }

  const status = getTemperatureStatus(temperature);

  const colorMap: Record<TemperatureStatus, string> = {
    Critical: theme.palette.error.dark,
    Warning: theme.palette.warning.main,
    Incident: theme.palette.info.main,
    Normal: theme.palette.success.main,
  };

  return {
    status,
    color: colorMap[status],
    severity: TEMPERATURE_SEVERITY[status],
  };
}

export function formatTemperature(
  temperature: number | null | undefined,
  precision = 1,
): string {
  if (temperature == null || !Number.isFinite(temperature)) {
    return '—';
  }
  return `${temperature.toFixed(precision)}°C`;
}

export function calculateStatistics(
  chartData: ReadonlyArray<[number, number | null]>,
): ChartStatistics | null {
  if (!chartData || chartData.length === 0) {
    return null;
  }

  const validValues = chartData
    .map((point) => point[1])
    .filter(
      (value): value is number => value != null && Number.isFinite(value),
    );

  if (validValues.length === 0) {
    return null;
  }

  const sorted = [...validValues].sort((a, b) => a - b);
  const count = validValues.length;

  const sum = validValues.reduce((acc, val) => acc + val, 0);
  const mean = sum / count;

  const squaredDiffs = validValues.map((val) => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / count;
  const stdDev = Math.sqrt(variance);

  const percentile = (p: number): number => {
    const index = Math.ceil((p / 100) * count) - 1;
    return sorted[Math.min(index, count - 1)];
  };

  return {
    min: sorted[0],
    max: sorted[count - 1],
    avg: mean,
    median: percentile(50),
    stdDev,
    count,
    latest: validValues[validValues.length - 1],
    totalPoints: chartData.length,
    percentile95: percentile(95),
    percentile99: percentile(99),
  };
}

export function calculateInterval(
  fromMs: number,
  toMs: number,
  targetPoints = DEFAULT_TARGET_POINTS,
): TimeInterval {
  const durationMs = Math.max(0, toMs - fromMs);
  const intervalMs = Math.max(1000, Math.floor(durationMs / targetPoints));

  if (intervalMs < TIME_UNIT_MS.minute) {
    const seconds = Math.max(1, Math.floor(intervalMs / TIME_UNIT_MS.second));
    return {
      value: seconds,
      unit: 'second',
    };
  }

  if (intervalMs < TIME_UNIT_MS.hour) {
    const minutes = Math.max(1, Math.floor(intervalMs / TIME_UNIT_MS.minute));
    return {
      value: minutes,
      unit: 'minute',
    };
  }

  if (intervalMs < TIME_UNIT_MS.day) {
    const hours = Math.max(1, Math.floor(intervalMs / TIME_UNIT_MS.hour));
    return {
      value: hours,
      unit: 'hour',
    };
  }

  const days = Math.max(1, Math.floor(intervalMs / TIME_UNIT_MS.day));
  return {
    value: days,
    unit: 'day',
  };
}

export function formatInterval(interval: TimeInterval): string {
  const { value, unit } = interval;
  const plural = value !== 1 ? 's' : '';
  return `${value} ${unit}${plural}`;
}

export interface InitialChartDates {
  dateFrom: string;
  dateTo: string;
  interval: TimeInterval;
}

export function calculateInitialChartDates(
  incidentTimestamp?: string,
): InitialChartDates {
  const now = dayjs.utc();
  let from: dayjs.Dayjs;

  if (incidentTimestamp) {
    from = dayjs.utc(incidentTimestamp).subtract(2, 'hours');
  } else {
    from = now.subtract(2, 'hours');
  }

  const to = now;
  const interval = calculateInterval(from.valueOf(), to.valueOf());

  return {
    dateFrom: from.format('YYYY-MM-DDTHH:mm:ss[Z]'),
    dateTo: to.format('YYYY-MM-DDTHH:mm:ss[Z]'),
    interval,
  };
}
